
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, CartesianGrid, ReferenceLine
} from 'recharts';
import {
  Users, Briefcase, CreditCard, DollarSign, Calendar,
  CheckCircle, Clock, Trash2, Edit2, Edit3, PlaySquare, AlertCircle, X,
  FileText, Search, Package, Repeat, TrendingUp, TrendingDown, Download, PieChart as PieChartIcon,
  Target, UserCheck, XCircle, Plus, ChevronDown, ChevronUp, Filter, ArrowUpRight, ArrowDownRight, Video
} from 'lucide-react';
import api, {
  getSubscriptions, getInvoices, getExpenses, getMonthlyReport, getAITools,
  getCurrentRate, setExchangeRate, formatIQD, formatUSD, createExpense, updateExpense, getExpenseCategories, updateInvoice,
  deleteExpense, recordPayment, createInvoice, getClients, getClient,
  createClient, getClientLTV, getOverdueInvoices, createSubscription, getAllPackages,
  generateSubInvoice, getProjects, createProject, updateProject,
  updateSubscription, deleteSubscription, cancelSubscription,
  cancelInvoice, reversePayment, getInvoice,
  updateProjectStatus, archiveProject, getProjectSummary,
  getInvoiceStatement, getDailySales, getDailyDeliveries,
  getUsers, createUser, updateUser, deleteUser,
  getDeliverables, createDeliverable, deleteDeliverable,
  deleteProject, deleteInvoice, getEmployees,
  generateTrackingToken
} from '../utils/api';
import toast from 'react-hot-toast';
import {
  EXCHANGE_RATE, S, iqd2usd, td, pill, btn, today, Dual, StatCard, FilterBtn, Modal, LabeledInput, DetailRow, EmptyRow, DateRangeFilter, VIDEO_TYPES, DeliveryModal, DeliveryEditModal, VT_LABEL, VT_FILTER_TABS
} from '../components/SharedUI';

export default function DeliverablesPage() {
  const [items, setItems] = useState([]);
  const [vtFilter, setVtFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingDeliverable, setEditingDeliverable] = useState(null);

  const load = useCallback(() => getDeliverables().then(setItems), []);
  useEffect(() => { load(); }, [load]);

  const thisMonth = new Date().toISOString().substring(0, 7);

  const filtered = vtFilter === 'all' ? items : items.filter(d => d.video_type === vtFilter);

  // Summary cards
  const totalCount = items.length;
  const secsThisMonth = items
    .filter(d => (d.delivered_at || d.delivery_date || '').startsWith(thisMonth))
    .reduce((s, d) => s + (d.seconds_used || d.total_seconds_used || 0), 0);
  const topType = (() => {
    const freq = {};
    items.forEach(d => { const t = d.video_type || 'ugc'; freq[t] = (freq[t] || 0) + 1; });
    const best = Object.entries(freq).sort((a, b) => b[1] - a[1])[0];
    return best ? (VT_LABEL[best[0]] || best[0]) : '—';
  })();
  const clientCount = new Set(items.map(d => d.client_id)).size;

  const summaryCards = [
    { label: 'إجمالي التسليمات', value: totalCount, icon: '📦', color: '#9B8EC4', bg: '#F0ECF7' },
    { label: `ثواني ${thisMonth}`, value: secsThisMonth + 'ث', icon: '⏱', color: '#17A589', bg: '#F0FDF4' },
    { label: 'أكثر نوع فيديو', value: topType, icon: '🎬', color: '#F59E0B', bg: '#FFFBEB' },
    { label: 'عملاء مخدومون', value: clientCount, icon: '👥', color: '#7C3AED', bg: '#EDE9FE' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="dark:!text-gray-100" style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-dark-h, #0F1E33)', display: 'flex', alignItems: 'center', gap: 8 }}>
          🎬 التسليمات
          <span className="dark:!text-gray-400" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted, #94A3B8)' }}>({items.length})</span>
        </h1>
        <button onClick={() => setShowModal(true)} style={btn('#17A589')}>
          <Plus size={15} /> تسليم جديد
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
        {summaryCards.map(c => (
          <div key={c.label} className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!shadow-none" style={{ background: c.bg, borderRadius: 12, padding: '16px 18px', border: `1px solid ${c.color}22` }}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{c.icon}</div>
            <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-gray, #64748B)', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Type filter tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-light, #E2E8F0)', marginBottom: 16, overflowX: 'auto' }}>
        {VT_FILTER_TABS.map(t => (
          <button key={t.key} type="button" onClick={() => setVtFilter(t.key)}
            style={{
              padding: '9px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontFamily: 'Tajawal, sans-serif', whiteSpace: 'nowrap',
              color: vtFilter === t.key ? '#9B8EC4' : 'var(--text-gray, #64748B)', fontWeight: vtFilter === t.key ? 700 : 400,
              borderBottom: `3px solid ${vtFilter === t.key ? '#9B8EC4' : 'transparent'}`,
              marginBottom: -2
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr className="dark:!bg-white/10 dark:!border-white/5 dark:!text-gray-100 dark:!shadow-none" style={{ background: 'var(--bg-flat-50, #F8FAFC)' }}>
                {['العميل / مُسلّم إلى', 'الباقة', 'عنوان الفيديو', 'المُنفّذ', 'النوع', 'الثواني', 'التاريخ', ''].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d, i) => (
                <tr key={d.id} className="dark:!bg-white/5 dark:!border-white/5 dark:!text-gray-100" style={{ borderBottom: '1px solid var(--border-light, #F1F5F9)', background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, white)' }}>
                  <td style={td({ fontWeight: 700 })}>
                    {d.client_id === 0 ? (
                      <span style={{ color: '#1E40AF', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        🏢 داخلي {d.submitted_to_name ? `➔ ${d.submitted_to_name}` : (d.submitted_to_id ? `➔ المدير` : '')}
                      </span>
                    ) : d.client_name}
                  </td>
                  <td style={td({ color: 'var(--text-gray, #64748B)', fontSize: 12 })}>{d.package_name || '—'}</td>
                  <td style={td()}>{d.title}</td>
                  <td style={td({ color: 'var(--text-gray, #475569)', fontSize: 12 })}>{d.worked_by_name || '—'}</td>
                  <td style={td()}>
                    <span style={pill('#F0ECF7', '#9B8EC4')}>{VT_LABEL[d.video_type] || d.video_type || '—'}</span>
                  </td>
                  <td style={td({ fontWeight: 700, color: '#17A589' })}>
                    {d.seconds_used || d.total_seconds_used}ث
                    {d.employee_points_multiplier > 1 && (
                      <span title={`مضاعف النقاط للموظف (x${d.employee_points_multiplier})`} style={{ display: 'inline-block', marginRight: 6, fontSize: 11, background: '#FEF3C7', color: '#B45309', padding: '2px 5px', borderRadius: 4 }}>⚡x{d.employee_points_multiplier}</span>
                    )}
                  </td>
                  <td style={td({ color: 'var(--text-gray, #64748B)', fontSize: 12 })}>{d.delivered_at || d.delivery_date}</td>
                  <td style={td()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setEditingDeliverable(d)}
                        title="تعديل التسليم"
                        className="dark:!bg-blue-900/30 dark:!text-blue-400"
                        style={{ background: '#E0F2FE', color: '#1E3A8A', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                        ✏️
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('حذف هذا التسليم؟')) return;
                          try { await deleteDeliverable(d.id); toast.success('تم الحذف'); load(); }
                          catch { toast.error('خطأ في الحذف'); }
                        }}
                        title="حذف التسليم"
                        className="dark:!bg-red-900/30 dark:!text-red-400"
                        style={{ background: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="dark:!text-gray-400" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted, #94A3B8)', fontSize: 14 }}>
                    لا توجد تسليمات
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <DeliveryModal
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); load(); }}
        />
      )}
      {editingDeliverable && (
        <DeliveryEditModal
          deliverable={editingDeliverable}
          onClose={() => setEditingDeliverable(null)}
          onSuccess={() => { setEditingDeliverable(null); load(); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUBSCRIPTIONS PAGE
// ═══════════════════════════════════════════════════════════════